import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Input,
  Button,
} from "reactstrap";
import { editSolicitud } from "../../../slices/thunks";
import { limpiarDatosSolicitud } from "../../../utils/solicitudDataCleaner";

/**
 * Parsea el string `solicitud.detalle` a un arreglo de items.
 * Formato esperado por línea:
 *   ✔️|❌ <medicamento> - CANT SOL <n> [- CANT EQU <m>]
 *
 * @param {string} detalle
 * @returns {Array<{aprobado: string, medicamento: string, cantidadSolicitada: string, cantidadEquivalente: string, parseable: boolean, raw: string}>}
 */
// Regex que reconoce los emojis aprobado/rechazado con o sin variation selector
// (U+FE0F) y distintas variantes visuales.
const APROBADO_RE = "(?:\\u2714\\uFE0F?|\\u2705)";
const RECHAZADO_RE = "(?:\\u274C|\\u2716\\uFE0F?)";
const PREFIJO_RE = new RegExp(`(${APROBADO_RE}|${RECHAZADO_RE})`, "u");
const SPLIT_RE = new RegExp(`(?=${APROBADO_RE}|${RECHAZADO_RE})`, "u");

// Acepta ambos formatos legados: "- CANT SOL 2" y "- Cant: 2"
const LINEA_RE = new RegExp(
  `^(${APROBADO_RE}|${RECHAZADO_RE})\\s*(.*?)\\s*-\\s*(?:CANT\\s*SOL|Cant)\\s*:?\\s*(\\d+)` +
    `(?:\\s*-\\s*(?:CANT\\s*EQU|Cant\\s*Eq)\\s*:?\\s*(\\d+))?\\s*$`,
  "u"
);

const parseDetalle = (detalle) => {
  if (!detalle || typeof detalle !== "string") return [];

  // Dividir por ✔️/✅ o ❌/✖️ (manteniendo el emoji al inicio de cada línea)
  const lineas = detalle
    .split(SPLIT_RE)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  return lineas.map((linea) => {
    const match = linea.match(LINEA_RE);
    if (match) {
      return {
        aprobado: match[1],
        medicamento: match[2].trim(),
        cantidadSolicitada: match[3],
        cantidadEquivalente: match[4] ?? "",
        parseable: true,
        raw: linea,
      };
    }
    return {
      aprobado: "",
      medicamento: linea,
      cantidadSolicitada: "",
      cantidadEquivalente: "",
      parseable: false,
      raw: linea,
    };
  });
};

/**
 * Serializa el arreglo de items de nuevo al formato de `detalle`,
 * agregando/actualizando ` - CANT EQU n` al final de cada línea parseable.
 */
const serializeDetalle = (items) => {
  return items
    .map((it) => {
      if (!it.parseable) return it.raw;
      const equivalente =
        it.cantidadEquivalente === "" || it.cantidadEquivalente === null || it.cantidadEquivalente === undefined
          ? 0
          : parseInt(it.cantidadEquivalente, 10) || 0;
      return `${it.aprobado} ${it.medicamento} - Cant: ${it.cantidadSolicitada} - Cant Eq: ${equivalente}`;
    })
    .join("\n");
};

const RegistroMedicamentosMercantil = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const bloqueado = useMemo(
    () => ["Entregado", "Cancelado"].includes(solicitud?.estatus),
    [solicitud?.estatus]
  );

  // Reinicializar al cambiar el detalle de la solicitud
  useEffect(() => {
    setItems(parseDetalle(solicitud?.detalle));
  }, [solicitud?.detalle]);

  const handleChangeEquivalente = (index, value) => {
    // Permitir vacío o entero >= 0 (sin límite superior)
    if (value !== "" && !/^\d+$/.test(value)) return;
    setItems((prev) =>
      prev.map((it, i) =>
        i === index ? { ...it, cantidadEquivalente: value } : it
      )
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const nuevoDetalle = serializeDetalle(items);
      const editado = limpiarDatosSolicitud({
        ...solicitud,
        detalle: nuevoDetalle,
      });
      await dispatch(editSolicitud(editado));
      toast.success("Cantidades equivalentes actualizadas", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        `Error al actualizar cantidades: ${error?.message || "desconocido"}`,
        { position: "top-right", autoClose: 5000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const hayEditables = items.some((it) => it.parseable);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardHeader className="bg-soft-primary">
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <i className="ri-medicine-bottle-line text-primary fs-22"></i>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="card-title mb-0 fw-semibold">
                  Registro de Medicamentos - Seguros Mercantil
                </h5>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-4">
            {items.length === 0 ? (
              <div className="alert alert-info d-flex align-items-center" role="alert">
                <i className="ri-information-line fs-18 me-2"></i>
                <div>No hay medicamentos para registrar.</div>
              </div>
            ) : (
              <>
                {bloqueado && (
                  <div className="alert alert-warning" role="alert">
                    <div className="d-flex">
                      <div className="flex-shrink-0">
                        <i className="ri-alert-line fs-18 me-2"></i>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">
                          Solicitud {solicitud?.estatus?.toLowerCase()}
                        </h6>
                        <p className="mb-0">
                          No es posible modificar las cantidades equivalentes porque la solicitud ya fue {solicitud?.estatus?.toLowerCase()}.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col" style={{ width: "60%" }}>
                          <div className="d-flex align-items-center">
                            <i className="ri-medicine-bottle-line text-primary me-1"></i>
                            Medicamento
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="text-center"
                          style={{ width: "20%" }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="ri-file-list-3-line text-primary me-1"></i>
                            Cantidad Solicitada
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="text-center"
                          style={{ width: "20%" }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="ri-capsule-line text-primary me-1"></i>
                            Cantidad Equivalente
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, index) => (
                        <tr key={index}>
                          <td>
                            {it.aprobado && (
                              <span className="me-2">{it.aprobado}</span>
                            )}
                            <span className="fw-medium">{it.medicamento}</span>
                            {!it.parseable && (
                              <span className="badge bg-warning-subtle text-warning ms-2">
                                Formato no reconocido
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            {it.parseable ? (
                              <span className="badge bg-primary-subtle text-primary fs-6">
                                {it.cantidadSolicitada}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-center">
                            {it.parseable ? (
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={it.cantidadEquivalente}
                                disabled={bloqueado || loading}
                                onChange={(e) =>
                                  handleChangeEquivalente(index, e.target.value)
                                }
                                style={{ maxWidth: "120px", margin: "0 auto" }}
                                className="text-center"
                                placeholder="0"
                              />
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!bloqueado && hayEditables && (
                  <Row className="mt-3">
                    <Col>
                      <div className="mb-3 mt-4 text-end">
                        <Button
                          type="button"
                          color="primary"
                          className="btn-lg px-4"
                          disabled={loading}
                          onClick={handleSubmit}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Guardando...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line align-middle fs-16 me-2"></i>
                              Guardar Cantidades Equivalentes
                            </>
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default RegistroMedicamentosMercantil;
